import { carrier, liesOn, type Shape } from './shape';
import type { Carrier } from './shape-ops';
import { add, cross, dist, distSq, dot, scale, sub, vec, type Vec2 } from './vec2';

const EPS = 1e-9;

type LineCarrier = Extract<Carrier, { kind: 'line' }>;
type CircleCarrier = Extract<Carrier, { kind: 'circle' }>;

export function intersect(a: Shape, b: Shape, eps = 1e-6): Vec2[] {
  return carrierIntersections(carrier(a), carrier(b)).filter(
    (p) => liesOn(a, p, eps) && liesOn(b, p, eps),
  );
}

function carrierIntersections(a: Carrier, b: Carrier): Vec2[] {
  if (a.kind === 'line') return b.kind === 'line' ? lineLine(a, b) : lineCircle(a, b);
  return b.kind === 'line' ? lineCircle(b, a) : circleCircle(a, b);
}

function lineLine(a: LineCarrier, b: LineCarrier): Vec2[] {
  const r = sub(a.b, a.a);
  const s = sub(b.b, b.a);
  const denom = cross(r, s);
  if (Math.abs(denom) < EPS) return [];
  const t = cross(sub(b.a, a.a), s) / denom;
  return [add(a.a, scale(r, t))];
}

// Measured from the foot of the perpendicular, not by the quadratic formula, which loses
// the points to cancellation on a very long line such as an extension guide.
function lineCircle(line: LineCarrier, c: CircleCarrier): Vec2[] {
  const d = sub(line.b, line.a);
  const length = Math.hypot(d.x, d.y);
  if (length < EPS) return [];
  const u = scale(d, 1 / length);
  const foot = add(line.a, scale(u, dot(sub(c.center, line.a), u)));
  const offSq = c.radius * c.radius - distSq(foot, c.center);
  if (offSq < 0) return [];
  if (offSq < EPS) return [foot];
  const along = scale(u, Math.sqrt(offSq));
  return [sub(foot, along), add(foot, along)];
}

function circleCircle(a: CircleCarrier, b: CircleCarrier): Vec2[] {
  const d = dist(a.center, b.center);
  if (d < EPS || d > a.radius + b.radius || d < Math.abs(a.radius - b.radius)) return [];
  const x = (d * d - b.radius * b.radius + a.radius * a.radius) / (2 * d);
  const hSq = a.radius * a.radius - x * x;
  if (hSq < 0) return [];
  const h = Math.sqrt(hSq);
  const dir = scale(sub(b.center, a.center), 1 / d);
  const base = add(a.center, scale(dir, x));
  if (h < EPS) return [base];
  const offset = vec(-dir.y * h, dir.x * h);
  return [add(base, offset), sub(base, offset)];
}
